import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { todayBR } from "../lib/dates";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Plus,
  LogOut,
  Trophy,
  User as UserIcon,
  Filter,
  Zap,
  Coins,
  Calendar as CalendarIcon,
  BarChart3,
  Wallet,
  Receipt,
  History,
  UserCircle2,
  ChevronDown,
  Menu,
  HandCoins,
} from "lucide-react";
import { toast } from "sonner";
import { OP_TYPES, opMeta } from "../lib/opTypes";
import StatsCards from "../components/StatsCards";
import EntriesTable from "../components/EntriesTable";
import EntryForm from "../components/EntryForm";
import BanksPanel from "../components/BanksPanel";
import ExpensesPanel from "../components/ExpensesPanel";
import PAPanel from "../components/PAPanel";
import Weekly365Panel from "../components/Weekly365Panel";
import GirosPanel from "../components/GirosPanel";
import LoansPanel from "../components/LoansPanel";

const MONTH_LABELS = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const TrophyIcon = () => <Trophy className="h-3.5 w-3.5 text-[#D4FF00]" strokeWidth={2.5} />;

function formatMonthLabel(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterMatch, setFilterMatch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all"); // "all" | "open"
  const [availableMatches, setAvailableMatches] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all"); // "all" or "YYYY-MM"
  const [periodType, setPeriodType] = useState("month"); // "all" | "day" | "week" | "month" | "year"
  const [periodDate, setPeriodDate] = useState(() => todayBR());
  const [periodYear, setPeriodYear] = useState(() => todayBR().slice(0, 4));
  const [accountHolder, setAccountHolder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState(null); // "YYYY-MM-DD" or null

  // Close mobile sidebar when tab changes
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [activeTab]);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== "all") params.operation_type = filterType;
      if (filterMatch !== "all") params.match = filterMatch;
      if (filterStatus === "open") params.finalized = false;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const statsParams = {};
      if (periodType === "all") {
        // legacy month selector still applies
        if (selectedMonth !== "all") statsParams.month = selectedMonth;
      } else if (periodType === "day") {
        statsParams.period = "day";
        statsParams.date = periodDate;
      } else if (periodType === "week") {
        statsParams.period = "week";
      } else if (periodType === "month") {
        statsParams.period = "month";
        statsParams.month = selectedMonth !== "all" ? selectedMonth : todayBR().slice(0, 7);
      } else if (periodType === "year") {
        statsParams.period = "year";
        statsParams.year = periodYear;
      }
      if (accountHolder !== "all") statsParams.account_holder = accountHolder;
      const [entriesRes, statsRes, matchesRes] = await Promise.all([
        api.get("/entries", { params }),
        api.get("/stats", { params: statsParams }),
        api.get("/matches"),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setAvailableMatches(matchesRes.data.matches || []);
    } catch (e) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [filterType, filterMatch, filterStatus, startDate, endDate, selectedMonth, periodType, periodDate, periodYear, accountHolder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNewEntry = () => {
    setEditingEntry(null);
    setDialogOpen(true);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Entrada excluída");
      loadData();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const availableMonths = stats?.available_months || [];

  return (
    <div className="grain relative min-h-screen bg-[#0A0A0A]">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-screen">
        {/* ============ MOBILE SIDEBAR OVERLAY ============ */}
        {mobileSidebarOpen && (
          <div
            data-testid="mobile-sidebar-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden"
          />
        )}

        {/* ============ SIDEBAR (desktop static + mobile drawer) ============ */}
        <aside
          data-testid="app-sidebar"
          className={`fixed left-0 top-0 z-50 flex h-screen w-64 shrink-0 flex-col border-r border-white/10 bg-[#0A0A0A] p-4 transition-transform duration-300 md:sticky md:top-0 md:z-auto md:w-60 md:translate-x-0 ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="mb-8 flex items-center gap-3 px-2 py-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#D4FF00]">
              <Trophy className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-lg font-black leading-none tracking-tight text-white">
                SURE FPS
              </div>
              <div className="mt-0.5 text-[0.62rem] uppercase tracking-widest text-zinc-500">
                Planilha esportiva
              </div>
            </div>
          </div>

          <TabsList
            data-testid="dashboard-tabs"
            className="flex flex-1 flex-col items-stretch gap-1 bg-transparent p-0"
          >
            {[
              { v: "dashboard", Icon: BarChart3, label: "Dashboard", tid: "tab-dashboard" },
              { v: "banks", Icon: Wallet, label: "Bancas", tid: "tab-banks" },
              { v: "expenses", Icon: Receipt, label: "Financeiro", tid: "tab-expenses" },
              { v: "weekly-365", Icon: CalendarIcon, label: "Semanal 365", tid: "tab-weekly-365" },
              { v: "pa", Icon: Zap, label: "Pagamento antecipado", tid: "tab-pa" },
              { v: "giros", Icon: Coins, label: "Giros grátis", tid: "tab-giros" },
              { v: "loans", Icon: HandCoins, label: "Empréstimos", tid: "tab-loans" },
              { v: "history", Icon: History, label: "Operações", tid: "tab-history" },
            ].map(({ v, Icon, label, tid }) => (
              <TabsTrigger
                key={v}
                data-testid={tid}
                value={v}
                className="justify-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-white data-[state=active]:bg-[#D4FF00]/10 data-[state=active]:font-bold data-[state=active]:text-[#D4FF00]"
              >
                <Icon className="h-4 w-4" strokeWidth={2.2} />
                <span>{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </aside>

        {/* ============ MAIN ============ */}
        <div className="flex-1">
          {/* Top bar */}
          <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md">
            <div className="flex items-center justify-between gap-3 px-3 py-3 md:gap-4 md:px-10 md:py-4">
              <div className="flex items-center gap-3">
                <button
                  data-testid="mobile-menu-btn"
                  onClick={() => setMobileSidebarOpen(true)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-zinc-300 transition hover:border-white/30 hover:text-white md:hidden"
                  aria-label="Abrir menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div>
                  <div className="text-[0.75rem] uppercase tracking-widest text-zinc-500">
                    Bem-vindo, {user?.name?.split(" ")[0]}
                  </div>
                  <div className="font-display text-xl font-bold text-white md:text-2xl">
                    {({
                      dashboard: "Dashboard",
                      banks: "Bancas",
                      expenses: "Financeiro",
                      "weekly-365": "Semanal 365",
                      pa: "Pagamento antecipado",
                      giros: "Giros grátis",
                      loans: "Empréstimos",
                      history: "Operações",
                    })[activeTab] || "Dashboard"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  data-testid="new-entry-btn"
                  onClick={handleNewEntry}
                  className="h-10 gap-2 rounded-full bg-[#D4FF00] px-4 font-bold text-black hover:bg-[#c4ee00] md:px-5"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <Plus className="h-5 w-5 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Nova operação</span>
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-btn"
                      className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 hover:border-white/30"
                      style={{ transition: "border-color 0.2s ease" }}
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <UserIcon className="h-4 w-4 text-zinc-400" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 border-white/10 bg-[#121212] text-zinc-200">
                    <DropdownMenuLabel>
                      <div className="font-normal">
                        <div className="text-sm font-semibold text-white">{user?.name}</div>
                        <div className="text-xs text-zinc-500">{user?.email}</div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem
                      data-testid="logout-btn"
                      onClick={logout}
                      className="cursor-pointer text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="relative z-10 space-y-6 px-3 py-5 md:space-y-8 md:px-10 md:py-10">
          {/* ============ DASHBOARD TAB ============ */}
          <TabsContent value="dashboard" className="mt-0 space-y-8">
            {/* Lucro Total hero card with period + holder filters */}
            <div
              data-testid="profit-hero"
              className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0F0F0F] p-6 md:p-8"
            >
              <div className="mb-6 flex flex-wrap items-center gap-2">
                {[
                  { v: "all", label: "Todos" },
                  { v: "day", label: "Diário" },
                  { v: "week", label: "Semanal" },
                  { v: "month", label: "Mensal" },
                  { v: "year", label: "Anual" },
                ].map((p) => {
                  const active = periodType === p.v;
                  return (
                    <button
                      key={p.v}
                      data-testid={`period-${p.v}`}
                      onClick={() => setPeriodType(p.v)}
                      className="h-9 rounded-full border px-4 text-xs font-semibold"
                      style={{
                        borderColor: active ? "#D4FF00" : "rgba(255,255,255,0.1)",
                        backgroundColor: active ? "#D4FF00" : "transparent",
                        color: active ? "#000" : "#D4D4D8",
                        transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                      }}
                    >
                      {p.label}
                    </button>
                  );
                })}
                {periodType === "day" && (
                  <Input
                    data-testid="period-day-date"
                    type="date"
                    value={periodDate}
                    onChange={(e) => setPeriodDate(e.target.value)}
                    className="h-9 w-[160px] bg-black/40 font-mono-num text-sm"
                  />
                )}
                {periodType === "month" && (
                  <Select value={selectedMonth === "all" ? new Date().toISOString().slice(0,7) : selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger data-testid="period-month-select" className="h-9 w-[160px] border-white/10 bg-black/40 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                      {availableMonths.length === 0 && (
                        <SelectItem value={new Date().toISOString().slice(0,7)}>
                          {new Date().toISOString().slice(0,7)}
                        </SelectItem>
                      )}
                      {availableMonths.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {periodType === "year" && (
                  <Select value={periodYear} onValueChange={setPeriodYear}>
                    <SelectTrigger data-testid="period-year-select" className="h-9 w-[120px] border-white/10 bg-black/40 font-mono-num text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                      {(stats?.available_years?.length ? stats.available_years : [String(new Date().getFullYear())]).map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <span className="mx-2 h-6 w-px bg-white/10" />
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 pl-3 pr-1">
                  <UserCircle2 className="h-3.5 w-3.5 text-zinc-500" />
                  <Select value={accountHolder} onValueChange={setAccountHolder}>
                    <SelectTrigger
                      data-testid="holder-filter"
                      className="h-9 w-[190px] border-0 bg-transparent text-sm shadow-none focus:ring-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                      <SelectItem value="all">Todos titulares</SelectItem>
                      {(stats?.available_holders || []).map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="text-[0.75rem] uppercase tracking-widest text-zinc-500">
                Lucro total · {stats?.period_label || "Todo o histórico"}
                {accountHolder !== "all" && (
                  <span className="ml-1 text-[#D4FF00]">· {accountHolder}</span>
                )}
              </div>
              <div
                data-testid="hero-total-profit"
                className="mt-2 font-mono-num text-6xl font-black tracking-tight md:text-7xl"
                style={{ color: (stats?.total_profit ?? 0) >= 0 ? "#00FF66" : "#FF3B30" }}
              >
                {(stats?.total_profit ?? 0) >= 0 ? "+" : "-"}R${" "}
                {Math.abs(stats?.total_profit ?? 0).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {stats?.total_entries || 0} operações
              </div>
            </div>

            <StatsCards
              stats={stats}
              weekly365={(() => {
                const isoWeekOf = (dateStr) => {
                  const d = new Date(dateStr + "T00:00:00");
                  d.setUTCHours(0, 0, 0, 0);
                  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
                  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
                  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
                };
                const currentWk = isoWeekOf(todayBR());
                const weeklyEntries = entries.filter((e) => e.weekly_365 && isoWeekOf(e.date) === currentWk);
                const stake = weeklyEntries.reduce(
                  (s, e) => s + (e.bookmakers || []).filter((b) => b.is_365).reduce((a, b) => a + (b.stake || 0), 0),
                  0
                );
                return { stake };
              })()}
            />

            {/* Calendar + Por tipo */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#121212] p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">Calendário</h3>
                    <p className="text-xs text-zinc-500">Lucro por dia — mês atual</p>
                  </div>
                </div>
                {(() => {
                  // Use Brasília date for "current month"
                  const [yStr, mStr] = todayBR().split("-");
                  const year = parseInt(yStr, 10);
                  const month = parseInt(mStr, 10) - 1;
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const byDay = {};
                  for (const e of entries) {
                    if (!e.date) continue;
                    const [ey, em, ed] = e.date.split("-").map(Number);
                    if (ey === year && em - 1 === month) {
                      byDay[ed] = (byDay[ed] || 0) + (e.profit || 0);
                    }
                  }
                  const cells = [];
                  for (let i = 0; i < firstDay; i++) cells.push(null);
                  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                  return (
                    <div className="space-y-2">
                      <div className="grid grid-cols-7 gap-1 text-center text-[0.62rem] uppercase tracking-widest text-zinc-600">
                        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                          <div key={d}>{d}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {cells.map((d, i) => {
                          if (d === null) return <div key={i} className="aspect-square" />;
                          const p = byDay[d] || 0;
                          const hasProfit = d in byDay;
                          const color = p >= 0 ? "#00FF66" : "#FF3B30";
                          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                          return (
                            <button
                              key={i}
                              type="button"
                              data-testid={`calendar-day-${iso}`}
                              onClick={() => hasProfit && setDayDetailDate(iso)}
                              disabled={!hasProfit}
                              className={`flex aspect-square flex-col justify-between rounded-md border p-1.5 text-[0.7rem] transition ${
                                hasProfit ? "cursor-pointer hover:scale-[1.05] hover:brightness-125" : "cursor-default"
                              }`}
                              style={{
                                borderColor: hasProfit ? `${color}55` : "rgba(255,255,255,0.06)",
                                backgroundColor: hasProfit ? `${color}12` : "rgba(0,0,0,0.3)",
                              }}
                            >
                              <span className="text-left font-semibold text-zinc-400">{d}</span>
                              {hasProfit && (
                                <span className="text-right font-mono-num text-[0.62rem] font-bold" style={{ color }}>
                                  {p >= 0 ? "+" : "-"}{Math.abs(p) >= 1000 ? `${Math.round(Math.abs(p)/100)/10}k` : Math.abs(p).toFixed(0)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
                <h3 className="font-display text-lg font-bold text-white">Por tipo</h3>
                <p className="mb-6 text-xs text-zinc-500">
                  {selectedMonth === "all" ? "Distribuição das operações" : "No período selecionado"}
                </p>
                {/* Donut chart */}
                {(() => {
                  const parts = OP_TYPES.map((p) => ({ key: p.key, label: p.short, color: p.color, value: stats?.count_by_type?.[p.key] || 0 }));
                  const total = parts.reduce((s, p) => s + p.value, 0) || 1;
                  let cum = 0;
                  const segments = parts.map((p) => {
                    const frac = p.value / total;
                    const dash = frac * 100;
                    const rot = (cum / total) * 360;
                    cum += p.value;
                    return { ...p, dash, rot };
                  });
                  return (
                    <div className="mb-4 flex justify-center">
                      <div className="relative h-32 w-32">
                        <svg viewBox="0 0 40 40" className="h-full w-full -rotate-90">
                          <circle cx="20" cy="20" r="15.915" fill="transparent" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
                          {segments.map((s) => (
                            <circle
                              key={s.key}
                              cx="20" cy="20" r="15.915"
                              fill="transparent"
                              stroke={s.color}
                              strokeWidth="4"
                              strokeDasharray={`${s.dash} 100`}
                              strokeDashoffset={-((s.rot / 360) * 100)}
                              style={{ transition: "stroke-dasharray 0.4s ease" }}
                            />
                          ))}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="font-mono-num text-2xl font-bold text-white">{stats?.total_entries || 0}</span>
                          <span className="text-[0.62rem] uppercase tracking-widest text-zinc-500">operações</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="space-y-4">
                  {OP_TYPES.map((t) => {
                    const count = stats?.count_by_type?.[t.key] || 0;
                    const total = stats?.total_entries || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={t.key} data-testid={`type-breakdown-${t.key}`}>
                        <div className="mb-1.5 flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-zinc-300">
                            <t.Icon className="h-3.5 w-3.5" style={{ color: t.color }} />
                            {t.short}
                          </span>
                          <span className="font-mono-num text-zinc-400">
                            {count} <span className="text-zinc-600">({pct}%)</span>
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: t.color,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Best day + Latest ops */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {(() => {
                const byDay = {};
                for (const e of entries) {
                  if (!e.date) continue;
                  byDay[e.date] = (byDay[e.date] || 0) + (e.profit || 0);
                }
                const best = Object.entries(byDay).sort((a, b) => b[1] - a[1])[0];
                return (
                  <div data-testid="best-day-card" className="rounded-xl border border-white/10 bg-gradient-to-br from-[#D4FF00]/10 to-[#141414] p-6 lg:col-span-1">
                    <div className="mb-2 flex items-center gap-2">
                      <TrophyIcon />
                      <span className="text-[0.75rem] uppercase tracking-widest text-[#D4FF00]">Melhor dia</span>
                    </div>
                    {best ? (
                      <>
                        <div className="font-mono-num text-3xl font-black text-white">
                          {best[0].split("-").reverse().join("/")}
                        </div>
                        <div className="mt-1 font-mono-num text-lg font-bold" style={{ color: best[1] >= 0 ? "#00FF66" : "#FF3B30" }}>
                          {best[1] >= 0 ? "+" : "-"}R$ {Math.abs(best[1]).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-zinc-500">Sem dados ainda</div>
                    )}
                  </div>
                );
              })()}

              <div data-testid="latest-ops-card" className="rounded-xl border border-white/10 bg-[#121212] p-6 lg:col-span-2">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">Últimas operações</h3>
                      <p className="text-xs text-zinc-500">6 mais recentes</p>
                    </div>
                    <button
                      onClick={() => setActiveTab("history")}
                      className="text-[0.7rem] font-semibold uppercase tracking-widest text-[#D4FF00] transition hover:text-white"
                      data-testid="latest-ops-view-all-btn"
                    >
                      Ver todas →
                    </button>
                  </div>
                  <div className="space-y-1">
                    {entries.slice(0, 6).map((e) => {
                      const [y, mo, da] = (e.date || "").split("-");
                      const dateFmt = y ? `${da}/${mo}` : "";
                      const teams = e.home_team && e.away_team ? `${e.home_team} × ${e.away_team}` : null;
                      const mainBk = e.bookmakers?.find((b) => b.is_main)?.name || e.bookmakers?.[0]?.name || "—";
                      const nBooks = e.bookmakers?.length || 0;
                      const profit = e.profit || 0;
                      const isPos = profit >= 0;

                      const tm = opMeta(e.operation_type);
                      const tc = { bg: `${tm.color}22`, fg: tm.color, label: tm.short, Icon: tm.Icon };

                      return (
                        <div
                          key={e.id}
                          data-testid={`latest-op-row-${e.id}`}
                          className="group flex items-center gap-3 rounded-lg border border-transparent px-2 py-2 transition hover:border-white/10 hover:bg-white/[0.02]"
                        >
                          {/* Left color bar */}
                          <div className="h-8 w-[3px] rounded-full" style={{ backgroundColor: isPos ? "#00FF66" : "#FF3B30" }} />
                          <div
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
                            style={{ backgroundColor: `${tm.color}26`, border: `1px solid ${tm.color}55` }}
                          >
                            <tc.Icon style={{ color: tm.color, width: 15, height: 15 }} strokeWidth={2.2} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-xs font-semibold text-zinc-200">
                                {teams || mainBk}
                              </span>
                              <span
                                className="shrink-0 rounded-sm px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider"
                                style={{ backgroundColor: tc.bg, color: tc.fg }}
                              >
                                {tc.label}
                              </span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-[0.7rem] text-zinc-500">
                              <span>{dateFmt}</span>
                              <span className="text-zinc-700">·</span>
                              <span className="truncate">{mainBk}</span>
                              {nBooks > 1 && (
                                <>
                                  <span className="text-zinc-700">·</span>
                                  <span>+{nBooks - 1}</span>
                                </>
                              )}
                            </div>
                          </div>

                          <div
                            className="font-mono-num shrink-0 text-sm font-bold tabular-nums"
                            style={{ color: isPos ? "#00FF66" : "#FF3B30" }}
                          >
                            {isPos ? "+" : "-"}R$ {Math.abs(profit).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      );
                    })}
                    {entries.length === 0 && (
                      <div className="py-4 text-center text-xs text-zinc-600">Sem operações ainda.</div>
                    )}
                  </div>
                </div>
            </div>
          </TabsContent>

          {/* ============ BANKS TAB ============ */}
          <TabsContent value="banks" className="mt-8">
            <BanksPanel />
          </TabsContent>

          {/* ============ EXPENSES TAB ============ */}
          <TabsContent value="expenses" className="mt-8">
            <ExpensesPanel />
          </TabsContent>

          {/* ============ PA TAB ============ */}
          <TabsContent value="pa" className="mt-8">
            <PAPanel />
          </TabsContent>

          {/* ============ WEEKLY 365 TAB ============ */}
          <TabsContent value="weekly-365" className="mt-8">
            <Weekly365Panel />
          </TabsContent>

          {/* ============ GIROS TAB ============ */}
          <TabsContent value="giros" className="mt-8">
            <GirosPanel />
          </TabsContent>

          <TabsContent value="loans" className="mt-8">
            <LoansPanel />
          </TabsContent>

          {/* ============ HISTORY TAB (Operações) ============ */}
          <TabsContent value="history" className="mt-8 space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-white">Operações</h2>
                <p className="text-xs text-zinc-500">
                  {filterStatus === "open" ? "Somente em aberto" : "Todas as suas operações registradas"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Status filter chips */}
                {[
                  { v: "all", label: "Todas" },
                  { v: "open", label: "Em aberto" },
                ].map((o) => {
                  const active = filterStatus === o.v;
                  return (
                    <button
                      key={o.v}
                      data-testid={`status-filter-${o.v}`}
                      onClick={() => setFilterStatus(o.v)}
                      className="h-9 rounded-full border px-4 text-xs font-semibold"
                      style={{
                        borderColor: active ? "#D4FF00" : "rgba(255,255,255,0.1)",
                        backgroundColor: active ? "#D4FF00" : "transparent",
                        color: active ? "#000" : "#D4D4D8",
                        transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
                <div className="flex items-center gap-1.5 rounded-md border border-white/10 bg-[#121212] px-2.5">
                  <Filter className="h-3.5 w-3.5 text-zinc-500" />
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger
                      data-testid="filter-type-select"
                      className="w-[10.5rem] border-0 bg-transparent text-sm shadow-none focus:ring-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="freebet">Freebet</SelectItem>
                      <SelectItem value="extracao_freebet">Extração Freebet</SelectItem>
                      <SelectItem value="proc_freebet">Procedimento de Freebet</SelectItem>
                      <SelectItem value="surebet">Surebet</SelectItem>
                      <SelectItem value="aumentada">Aumentada %</SelectItem>
                      <SelectItem value="giros">Giros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select value={filterMatch} onValueChange={setFilterMatch}>
                  <SelectTrigger
                    data-testid="filter-match-select"
                    className="w-[220px] border-white/10 bg-[#121212] text-sm"
                  >
                    <SelectValue placeholder="Todos confrontos" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] border-white/10 bg-[#121212] text-zinc-200">
                    <SelectItem value="all">Todos confrontos</SelectItem>
                    {availableMatches.map((m) => {
                      const home = (m?.home || "").trim();
                      const away = (m?.away || "").trim();
                      if (!home || !away) return null;
                      const value = `${home}|${away}`;
                      const label = `${home} × ${away}`;
                      return (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="filter-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[150px] bg-[#121212] font-mono-num text-sm"
                />
                <Input
                  data-testid="filter-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[150px] bg-[#121212] font-mono-num text-sm"
                />
                {(filterType !== "all" || filterMatch !== "all" || filterStatus !== "all" || startDate || endDate) && (
                  <Button
                    data-testid="clear-filters-btn"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilterType("all");
                      setFilterMatch("all");
                      setFilterStatus("all");
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="text-xs text-zinc-500 hover:bg-white/5 hover:text-white"
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />
            ) : (
              <>
                {(() => {
                  // Sort: open bets first, then finalized. Within each group by date desc.
                  const sorted = [...entries].sort((a, b) => {
                    const aOpen = !a.finalized ? 0 : 1;
                    const bOpen = !b.finalized ? 0 : 1;
                    if (aOpen !== bOpen) return aOpen - bOpen;
                    return (b.date || "").localeCompare(a.date || "");
                  });
                  const totalRevenue = sorted.reduce((s, e) => s + (e.revenue || 0), 0);
                  const totalProfit = sorted.reduce((s, e) => s + (e.profit || 0), 0);
                  const openCount = sorted.filter((e) => !e.finalized).length;
                  return (
                    <>
                      <div
                        data-testid="operations-summary"
                        className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-[#121212] p-4 md:grid-cols-4"
                      >
                        <SummaryStat label="Operações" value={sorted.length} />
                        <SummaryStat label="Em aberto" value={openCount} highlight={openCount > 0 ? "#F59E0B" : null} />
                        <SummaryStat
                          label="Receita total"
                          value={`R$ ${totalRevenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          testid="operations-total-revenue"
                        />
                        <SummaryStat
                          label="Lucro total"
                          value={`${totalProfit >= 0 ? "+" : "-"}R$ ${Math.abs(totalProfit).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          highlight={totalProfit >= 0 ? "#00FF66" : "#FF3B30"}
                        />
                      </div>
                      <EntriesTable
                        entries={sorted}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        showFinalized
                        onToggleFinalized={async (id, val) => {
                          try {
                            await api.patch(`/entries/${id}/finalize`, { finalized: val });
                            setEntries((prev) => prev.map((x) => (x.id === id ? { ...x, finalized: val } : x)));
                          } catch {
                            toast.error("Erro ao atualizar status");
                          }
                        }}
                        onEntryChanged={(updated) => {
                          setEntries((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                        }}
                      />
                    </>
                  );
                })()}
              </>
            )}
          </TabsContent>
      </main>
        </div>
      </Tabs>

      <EntryForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editingEntry}
        onSaved={loadData}
      />

      {/* Day-detail modal */}
      <DayDetailModal
        date={dayDetailDate}
        onClose={() => setDayDetailDate(null)}
        entries={entries}
      />
    </div>
  );
}

/* ---------- Day detail modal ---------- */
function SummaryStat({ label, value, highlight, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[0.62rem] uppercase tracking-widest text-zinc-500 md:text-[0.7rem]">{label}</span>
      <span
        className="font-mono-num text-base font-bold tabular-nums md:text-lg"
        style={{ color: highlight || "#F4F4F5" }}
      >
        {value}
      </span>
    </div>
  );
}

function DayDetailModal({ date, onClose, entries }) {
  const dayEntries = (entries || []).filter((e) => e.date === date);
  const totalProfit = dayEntries.reduce((s, e) => s + (e.profit || 0), 0);
  const totalRevenue = dayEntries.reduce((s, e) => s + (e.revenue || 0), 0);

  const dateFmt = date ? date.split("-").reverse().join("/") : "";

  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="day-detail-modal"
        className="max-w-2xl border-white/10 bg-[#121212] text-zinc-200"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">
            Operações — {dateFmt}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {dayEntries.length} operação{dayEntries.length === 1 ? "" : "es"} · Receita total{" "}
            <span className="font-mono-num text-zinc-300">
              R$ {totalRevenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>{" "}
            · Lucro{" "}
            <span className="font-mono-num font-bold" style={{ color: totalProfit >= 0 ? "#00FF66" : "#FF3B30" }}>
              {totalProfit >= 0 ? "+" : "-"}R$ {Math.abs(totalProfit).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {dayEntries.length === 0 && (
            <div className="py-8 text-center text-sm text-zinc-500">Sem operações neste dia.</div>
          )}
          {dayEntries.map((e) => {
            const teams = e.home_team && e.away_team ? `${e.home_team} × ${e.away_team}` : null;
            const mainBk = e.bookmakers?.find((b) => b.is_main)?.name || e.bookmakers?.[0]?.name || "—";
            const tm = opMeta(e.operation_type);
            const tc = { bg: `${tm.color}22`, fg: tm.color, label: tm.short };
            const profit = e.profit || 0;
            const isPos = profit >= 0;
            return (
              <div
                key={e.id}
                data-testid={`day-detail-row-${e.id}`}
                className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 px-3 py-2.5"
              >
                <div className="h-8 w-[3px] rounded-full" style={{ backgroundColor: isPos ? "#00FF66" : "#FF3B30" }} />
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
                  style={{ backgroundColor: `${tm.color}26`, border: `1px solid ${tm.color}55` }}
                >
                  <tm.Icon style={{ color: tm.color, width: 15, height: 15 }} strokeWidth={2.2} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-white">{teams || mainBk}</span>
                    <span
                      className="shrink-0 rounded-sm px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: tc.bg, color: tc.fg }}
                    >
                      {tc.label}
                    </span>
                    {!e.finalized && (
                      <span className="shrink-0 rounded-sm bg-orange-500/20 px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-orange-400">
                        Em aberto
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[0.75rem] text-zinc-500">
                    {mainBk} · Receita R$ {(e.revenue || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="font-mono-num text-sm font-bold tabular-nums" style={{ color: isPos ? "#00FF66" : "#FF3B30" }}>
                  {isPos ? "+" : "-"}R$ {Math.abs(profit).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
